import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../contexts/AuthContext';
import { firebaseService } from '../services/firebaseService';

const AccountSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithMagicLink, clearError } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const currentUrl = useMemo(() => window.location.href, []);
  const email = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get('email') || localStorage.getItem('emailForSignIn') || '').trim().toLowerCase();
  }, [location.search]);

  const hasValidMagicLink = useMemo(() => {
    return firebaseService.isMagicLinkSignIn(currentUrl);
  }, [currentUrl]);

  const validate = () => {
    if (!email) {
      setLocalError('Invite link is missing the target email. Ask admin for a new invitation.');
      return false;
    }

    if (!hasValidMagicLink) {
      setLocalError('Invite link is invalid or expired. Ask admin for a new invitation.');
      return false;
    }

    if (!password || !confirmPassword) {
      setLocalError('Both password fields are required.');
      return false;
    }

    if (password !== confirmPassword) {
      setLocalError('Password and confirm password do not match.');
      return false;
    }

    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    clearError();
    setLocalError('');

    if (!validate()) {
      return;
    }

    setIsLoading(true);

    try {
      // Use the invite magic link to sign in first, then set password in Firebase.
      const loginResult = await loginWithMagicLink(email, currentUrl);
      if (!loginResult.success) {
        setLocalError(loginResult.error || 'Failed to authenticate invite link.');
        return;
      }

      const passwordResult = await firebaseService.setCurrentUserPassword(password);
      if (!passwordResult.success) {
        setLocalError('Failed to set your password. Please try again.');
        return;
      }

      await firebaseService.markPasswordConfigured();

      localStorage.setItem('emailForSignIn', email);
      navigate('/dashboard', { replace: true });
    } catch (error) {
      setLocalError(error?.message || 'Failed to complete account setup. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-600">
            <span className="text-2xl font-bold text-white">B</span>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Set Up Your Account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Create your password to finish sign-in.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                disabled
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 text-gray-900 rounded-md bg-gray-100 sm:text-sm"
              />
            </div>

            <div className="relative">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 text-gray-900 rounded-md sm:text-sm pr-10"
                placeholder="Enter password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 top-6 pr-3 flex items-center"
                onClick={() => setShowPassword((prev) => !prev)}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <div className="relative">
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 text-gray-900 rounded-md sm:text-sm pr-10"
                placeholder="Confirm password"
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 top-6 pr-3 flex items-center"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
              >
                <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            <p className="text-xs text-gray-500">
              Password policy is enforced by Firebase Identity Platform.
            </p>

            <button
              type="submit"
              disabled={isLoading || !hasValidMagicLink}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Setting password...' : 'Set password and continue'}
            </button>
          </div>

          {localError && (
            <div className="rounded-md bg-red-50 p-4 border border-red-200">
              <p className="text-sm font-medium text-red-800">{localError}</p>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default AccountSetup;
